// Dependency-free sorted horizontal bar chart. One row per item (here: a branch),
// ranked by current value descending. When compare=true, each row shows the
// previous period as a grey ghost bar beneath the current bar, plus a MoM delta.
//
// Drop-in sibling of RadarChart: same axes shape ({ key, label, value, prev }),
// same color / prevColor / compare / height / compact / emptyText props.

function fmtShort(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`
  return `${Math.round(v)}`
}
function fmtFull(n) { return Math.round(Number(n) || 0).toLocaleString('en-US') }

export default function BarCompareChart({
  axes = [], color = '#2563eb', prevColor = '#94a3b8',
  compare = false, height = 320, compact = true, emptyText = 'ไม่มีข้อมูล',
}) {
  const fmtVal = compact ? fmtShort : fmtFull
  if (!axes.length) {
    return <div className="flex items-center justify-center text-[13px] text-slate-400" style={{ height }}>{emptyText}</div>
  }

  const rows = [...axes].sort((a, b) => (b.value || 0) - (a.value || 0))
  // scale to the largest bar shown (current, and previous when comparing)
  const max = Math.max(...rows.map((r) => Math.max(r.value || 0, compare ? (r.prev || 0) : 0)), 1)
  const pct = (v) => `${Math.max(0, ((v || 0) / max) * 100)}%`

  return (
    <div className="overflow-y-auto pr-1" style={{ maxHeight: height }}>
      <div className="space-y-2">
        {rows.map((r) => {
          const val = r.value || 0
          const prev = r.prev || 0
          const diff = val - prev
          const up = diff >= 0
          const deltaPct = prev > 0 ? Math.round((diff / prev) * 100) : null
          return (
            <div key={r.key ?? r.label} className="grid grid-cols-[96px_1fr_auto] items-center gap-2 text-[11px]">
              <div className="truncate text-right text-slate-500" title={r.label}>{r.label}</div>
              <div className="min-w-0">
                {/* current bar */}
                <div className="h-2.5 rounded-sm" style={{ width: pct(val), background: color, minWidth: val > 0 ? 2 : 0 }} />
                {/* previous ghost bar */}
                {compare && (
                  <div className="mt-0.5 h-1.5 rounded-sm" style={{ width: pct(prev), background: prevColor, opacity: 0.5, minWidth: prev > 0 ? 2 : 0 }} />
                )}
              </div>
              <div className="text-right tabular-nums leading-tight">
                <div className="font-semibold" style={{ color }}>{fmtVal(val)}</div>
                {compare && (
                  <div className="text-[10px] flex items-center justify-end gap-1">
                    <span className="text-slate-400">{fmtVal(prev)}</span>
                    {deltaPct == null
                      ? <span className="text-emerald-500 font-medium">ใหม่</span>
                      : <span className={up ? 'text-emerald-600 font-medium' : 'text-rose-500 font-medium'}>{up ? '▲' : '▼'}{Math.abs(deltaPct)}%</span>}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
